import { useCallback, useEffect, useRef, useState } from 'react';

export interface Kpis {
  chargersTotal: number;
  chargersOnline: number;
  availabilityPct: number;
  activeTransactions: number;
  energyLast24hWh: number;
  recentTransactions: Array<{
    ocppTransactionId: number;
    cpId: string;
    connectorId: number;
    idTag: string;
    startTime: string;
    stopTime: string | null;
    energyWh: number | null;
    stopReason: string | null;
  }>;
}

async function fetchKpis(): Promise<Kpis> {
  const res = await fetch('/api/dashboard/kpis', { credentials: 'include' });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as Kpis;
}

/**
 * Assina o /ws/dashboard e refaz o fetch quando um evento chega.
 * Fallback curto para manter o painel operacional mesmo se o WebSocket cair.
 */
export function useKpis() {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  const refresh = useCallback(
    () =>
      fetchKpis()
        .then((k) => {
          setKpis(k);
          setError(null);
        })
        .catch((e: Error) => setError(e.message)),
    [],
  );

  useEffect(() => {
    void refresh();
    // A especificação do painel exige leitura em cadência de um segundo.
    // Com WebSocket ativo, os eventos normalmente antecipam essa atualização.
    const poll = setInterval(() => void refresh(), 1_000);

    // tenta WS
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/dashboard`);
    wsRef.current = ws;
    ws.addEventListener('open', () => setLive(true));
    ws.addEventListener('close', () => setLive(false));
    ws.addEventListener('error', () => setLive(false));
    ws.addEventListener('message', () => void refresh());

    return () => {
      clearInterval(poll);
      ws.close();
    };
  }, [refresh]);

  return { kpis, error, live, refresh };
}
